import { ref } from "vue";
import { defineStore } from "pinia";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import { Copy, Trash } from "lucide-vue-next";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@composables/handleError.js";
import { captureEvent } from "@/lib/analytics";

export const useSurveysStore = defineStore("surveys", () => {
  // state
  const surveys = ref([]);
  const lightSurveys = ref([]);
  const lightSurvey = ref({});
  const currentPage = ref(1);
  const surveysCount = ref(0);
  const statusCounts = ref({});
  const isLoading = ref(false);
  const surveysSearchQuery = ref("");
  const surveysStatusFilter = ref([]);
  const surveysMineOnlyFilter = ref(false);
  const surveysAccountsFilter = ref([]);
  const surveysStudyTypeFilter = ref([]);
  const surveysShowFreemiumDemoFilter = ref(false);
  const actionDropdownSurveys = ref([]);

  // actions
  async function fetch(page = 1) {
    const { response, data } = await useMyFetch(`/surveys?page=${page}`).json();

    if (response.value.ok) {
      const { surveys: surveysData, surveys_count: surveyCountData, status_counts: statusCountsData } = data.value;
      surveys.value = surveysData;
      surveysCount.value = surveyCountData;
      statusCounts.value = statusCountsData || {};
    }

    return surveys.value;
  }

  const getSurveyLight = async (surveyId) => {
    const url = `/surveys/light/${surveyId}`;
    const { response, data } = await useMyFetch(url).json().get();

    if (response.value.ok) {
      lightSurvey.value = data.value;
      if (!lightSurveys.value.some((s) => s.id === lightSurvey.value.id)) {
        lightSurveys.value.push(lightSurvey.value);
      }
    }
    return response.value.ok;
  };

  const fetchSurveysLight = async (page = null, name = null, status = null, accountIds = []) => {
    const params = new URLSearchParams();
    if (page !== null) params.append("page", page);
    if (name !== null) params.append("name", name);
    if (status !== null) params.append("status", status);
    for (const id of accountIds) params.append("account_ids[]", id);

    const { response, data, error } = await useMyFetch(`/surveys/light?${params.toString()}`).json();
    if (error.value || !response.value.ok) throw error.value || new Error("Failed to fetch surveys light");
    return data.value;
  };

  /**
   * @param {number | null} page
   * @param {string | null} name
   */
  const getSurveysLight = async (page = null, name = null) => {
    const data = await fetchSurveysLight(page, name).catch(() => null);
    if (!data) return;

    lightSurveys.value = data.surveys || [];
    if (lightSurvey.value.id) {
      if (!lightSurveys.value.some((s) => s.id === lightSurvey.value.id)) {
        lightSurveys.value.push(lightSurvey.value);
      }
    }
  };

  /**
   * @param {number} page
   * @param {string} search
   * @param {string | null} status
   * @param {number[]} accountIds
   */
  const fetchLightSurveyOptionsPage = async (page, search, status = null, accountIds = []) => {
    const data = await fetchSurveysLight(page, search || null, status, accountIds);
    const known = new Set(lightSurveys.value.map((s) => s.id));
    lightSurveys.value.push(...(data.surveys || []).filter((s) => !known.has(s.id)));
    return {
      options: (data.surveys || []).map((survey) => ({ label: survey.name, value: survey.id })),
      count: data.surveys_count || 0,
    };
  };

  async function search(page = 1) {
    const params = {
      status: surveysStatusFilter.value,
      accounts: surveysAccountsFilter.value,
      study_type: surveysStudyTypeFilter.value,
      mine_only: surveysMineOnlyFilter.value || undefined,
      show_freemium_demo: surveysShowFreemiumDemoFilter.value || undefined,
      name: surveysSearchQuery.value,
      page: page,
    };

    isLoading.value = true;
    try {
      const { response, data } = await useMyFetch(`/surveys/search`).post(params, null).json();

      if (response.value.ok) {
        const { surveys: surveysData, surveys_count: surveyCountData, status_counts: statusCountsData } = data.value;
        surveys.value = surveysData;
        surveysCount.value = surveyCountData;
        statusCounts.value = statusCountsData || {};
        currentPage.value = page;
      }

      return surveys.value;
    } catch (error) {
      return handleError(error);
    } finally {
      isLoading.value = false;
    }
  }

  async function duplicateSurvey(token) {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token}/duplicate`).post().json();

      if (response.value.ok) {
        surveys.value.splice(0, 0, data.value);
        captureEvent("survey_duplicated", { survey_token: token, duplicate_token: data.value?.token });
      }
    } catch (err) {
      handleError(err);
    }
  }

  async function removeSurvey(token) {
    const { response } = await useMyFetch(`/surveys/${token}`).delete().json();

    if (response.value.ok) {
      // If the survey is deleted, remove it from the list
      const surveyIndex = surveys.value.findIndex((s) => s.token === token);
      if (surveyIndex !== -1) surveys.value.splice(surveyIndex, 1);

      captureEvent("survey_deleted", { survey_token: token });
    }
  }

  const toggleMineOnlyFilter = async () => {
    surveysMineOnlyFilter.value = !surveysMineOnlyFilter.value;
    await search();
  };

  const toggleShowFreemiumDemoFilter = async () => {
    surveysShowFreemiumDemoFilter.value = !surveysShowFreemiumDemoFilter.value;
    await search();
  };

  const clearFilters = async () => {
    resetAllFilters();
    await search();
  };

  const changePage = async ({ currentPage: newPage }) => {
    currentPage.value = newPage;
    await search(newPage);
  };

  const resetAllFilters = () => {
    surveysStatusFilter.value = [];
    surveysMineOnlyFilter.value = false;
    surveysAccountsFilter.value = [];
    surveysStudyTypeFilter.value = [];
    surveysShowFreemiumDemoFilter.value = false;
    resetSearchQuery();
    currentPage.value = 1;
  };

  const selectLightSurvey = (surveyId) => {
    const survey = lightSurveys.value.find((s) => s.id === surveyId);
    if (survey) lightSurvey.value = survey;
  };

  // getters
  actionDropdownSurveys.value = [
    {
      isDelete: false,
      function: duplicateSurvey,
      text: "duplicate",
      icon: Copy,
    },
    {
      isDelete: true,
      function: removeSurvey,
      msgConfirm: "this survey",
      text: "delete",
      icon: Trash,
    },
  ];

  const { resetSearchQuery, setSearchQuery } = useDebouncedSearchQuery(surveysSearchQuery, () => search());

  return {
    // state
    surveys,
    currentPage,
    surveysCount,
    statusCounts,
    isLoading,
    surveysSearchQuery,
    surveysStatusFilter,
    surveysMineOnlyFilter,
    surveysAccountsFilter,
    surveysStudyTypeFilter,
    surveysShowFreemiumDemoFilter,
    lightSurvey,
    lightSurveys,
    fetchLightSurveyOptionsPage,
    actionDropdownSurveys,

    // actions
    fetch,
    search,
    setSearchQuery,
    toggleMineOnlyFilter,
    toggleShowFreemiumDemoFilter,
    clearFilters,
    changePage,
    resetAllFilters,
    selectLightSurvey,
    getSurveysLight,
    getSurveyLight,
  };
});
